import React, { useEffect, useRef, useState } from 'react';
import { Space, RadioChangeEvent } from 'antd';
import { DatePicker, Radio, message } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { DateType, DateTypeDisplay } from '@/pages/EnergyUsageCostKanban/prop';
import { isNil } from 'lodash-es';
import { useSearchParams } from 'react-router-dom';
export type RangeValue = [Dayjs | null, Dayjs | null] | null;
const RangePicker = DatePicker.RangePicker;

export interface SwitchRangePickerProps {
  time?: RangeValue;
  setTime?: (time?: RangeValue) => void;
  timeResolution?: DateType;
  setTimeResolution?: (value: DateType) => void;
  setDataQueryPeriodId?: (value?: number) => void;
}

const SwitchRangePicker = ({
  setTime,
  timeResolution,
  setTimeResolution,
  setDataQueryPeriodId,
}: SwitchRangePickerProps) => {
  const [value, setValue] = useState<RangeValue>();
  const [disPlayValue, setDisplayValue] = useState<RangeValue>();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const startDate = urlSearchParams.get('startDate') ?? undefined;
  const endDate = urlSearchParams.get('endDate') ?? undefined;
  const currentYear = urlSearchParams.get('currentYear') ?? false;
  const currentMonth = urlSearchParams.get('currentMonth') ?? false;
  const hasInitRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isNil(disPlayValue)) {
      let startTime = disPlayValue?.[0]?.startOf('day');
      let endTime = disPlayValue?.[1]?.isToday() ? dayjs() : disPlayValue?.[1]?.endOf('day');

      switch (timeResolution) {
        case DateType.WEEK:
          startTime = disPlayValue?.[0]?.startOf('week').startOf('day');
          endTime = disPlayValue?.[1]?.endOf('week').endOf('day');

          break;
        case DateType.MONTH:
          startTime = disPlayValue?.[0]?.startOf('month').startOf('day');
          endTime = disPlayValue?.[1]?.endOf('month').endOf('day');
          break;
        case DateType.YEAR:
          startTime = disPlayValue?.[0]?.startOf('year').startOf('day');
          endTime = disPlayValue?.[1]?.endOf('year').endOf('day');
          break;
      }
      setTime?.([startTime!, endTime!]);
      setValue([startTime!, endTime!]);
    }
  }, [disPlayValue]);

  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    if (currentYear === 'true') {
      // 按年
      setTimeResolution?.(DateType.YEAR);
      if (startDate && endDate) {
        const s = dayjs(startDate, 'YYYY').startOf('year');
        const e = dayjs(endDate, 'YYYY').endOf('year');

        setDisplayValue([s, e]);
        setValue([s, e]);
        setTime?.([s, e]);
      } else {
        const start = dayjs().startOf('year');
        const end = dayjs().endOf('year');

        setDisplayValue([start, end]);
        setValue([start, end]);
        setTime?.([start, end]);
      }
    } else if (currentMonth === 'true') {
      // 按月
      setTimeResolution?.(DateType.MONTH);

      if (startDate && endDate) {
        const s = dayjs(startDate, 'YYYYMM').startOf('month');
        const e = dayjs(endDate, 'YYYYMM').endOf('month');

        setDisplayValue([s, e]);
        setValue([s, e]);
        setTime?.([s, e]);
      }
    } else {
      // 按日
      setTimeResolution?.(DateType.DAY);

      if (startDate && endDate) {
        const s = dayjs(startDate, 'YYYYMMDD').startOf('day');
        const e = dayjs(endDate, 'YYYYMMDD').endOf('day');

        setDisplayValue([s, e]);
        setValue([s, e]);
        setTime?.([s, e]);
      } else {
        const s = dayjs(dayjs().subtract(31, 'day'), 'YYYYMMDD').startOf('day');
        const e = dayjs(dayjs().subtract(1, 'day'), 'YYYYMMDD').endOf('day');

        setDisplayValue([s, e]);
        setValue([s, e]);
        setTime?.([s, e]);
      }
    }
  }, []);

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    setTimeResolution?.(value);
    if (value) {
      switch (value) {
        case DateType.DAY:
          setDisplayValue?.([dayjs().subtract(31, 'day'), dayjs().subtract(1, 'day')]);
          break;
        case DateType.WEEK:
          setDisplayValue([dayjs().subtract(10, 'week'), dayjs()]);

          break;
        case DateType.MONTH:
          setDisplayValue([dayjs().subtract(12, 'month'), dayjs()]);
          break;
        case DateType.YEAR:
          setDisplayValue([dayjs().subtract(6, 'year'), dayjs()]);
          break;
      }
    }
    setValue(undefined);
    setTime?.(undefined);
    setDataQueryPeriodId?.(undefined);
  };

  const disabledDate = (current: Dayjs) => {
    if (timeResolution === DateType.DAY) {
      return current && current >= dayjs().startOf('day');
    } else {
      let tooLate = null;
      let tooEarly = null;
      const afterToday = current && current > dayjs().endOf('day');
      if (DateType.MONTH === timeResolution) {
        tooLate = value?.[0] && current.diff(value?.[0], 'months') >= 23;
        tooEarly = value?.[1] && value?.[1].diff(current, 'months') >= 23;
      }
      return !!tooEarly || !!tooLate || !!afterToday;
    }
  };

  return (
    <Space size={16}>
      <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
        <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
        <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
        <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
        <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
      </Radio.Group>
      {timeResolution === DateType.DAY && (
        <RangePicker
          picker="date"
          style={{ width: '250px' }}
          value={value}
          disabledDate={disabledDate}
          onChange={dates => {
            if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
              message.error('最多选择31天');
              return;
            }
            setDataQueryPeriodId?.(undefined);
            setDisplayValue(dates);
          }}
          allowClear={false}
        />
      )}
      {timeResolution === DateType.WEEK && (
        <RangePicker
          picker="week"
          style={{ width: '250px' }}
          value={value}
          disabledDate={disabledDate}
          onChange={dates => {
            if (dates?.[0] && dates?.[1] && dates?.[0].add(10, 'week').valueOf() < dates?.[1].valueOf()) {
              message.error('最多选择10周');
              return;
            }
            setDataQueryPeriodId?.(undefined);
            setDisplayValue(dates);
          }}
          allowClear={false}
        />
      )}
      {timeResolution === DateType.MONTH && (
        <RangePicker
          picker="month"
          style={{ width: '250px' }}
          value={value}
          disabledDate={disabledDate}
          onChange={dates => {
            console.log('onChange', dates);
            if (dates?.[0] && dates?.[1] && dates?.[0].add(24, 'month').valueOf() < dates?.[1].valueOf()) {
              message.error('最多选择24个月');
              return;
            }
            setDataQueryPeriodId?.(undefined);
            setDisplayValue(dates);
          }}
          allowClear={false}
        />
      )}
      {timeResolution === DateType.YEAR && (
        <RangePicker
          picker="year"
          style={{ width: '250px' }}
          value={value}
          disabledDate={disabledDate}
          onChange={dates => {
            if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
              message.error('最多选择6年');
              return;
            }
            setDataQueryPeriodId?.(undefined);
            setDisplayValue(dates);
          }}
          allowClear={false}
        />
      )}
    </Space>
  );
};

export default SwitchRangePicker;
